// @flow
import type { Action, State } from './types';

import { DEFAULT_WATCH_LIST_SORT } from './actions';
import {
  sortWatchlistByName,
  sortWatchListByDateAdded,
  populateContentIdsSet,
  filterOutRemovedWatchlistItem,
} from './utils';

export const initialState: State = {
  list: {
    fetching: false,
    error: false,
    content: [],
  },
  sort: DEFAULT_WATCH_LIST_SORT,
  contentIdsIncludedToWatchlist: {},
  watchlistIsStale: false,
  mappingWasInitialized: false,
};

const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_WATCHLIST': {
      const { content } = state.list;
      return {
        ...state,
        list: {
          fetching: true,
          error: false,
          /* Keep results of previous fetches */
          content,
        },
      };
    }
    case 'RECEIVE_WATCHLIST': {
      const { sort } = state;
      const { payload } = action;

      const content =
        sort === 'name'
          ? sortWatchlistByName(payload.items)
          : sortWatchListByDateAdded(payload.items);

      return {
        ...state,
        list: {
          fetching: false,
          error: false,
          content,
        },
        // TODO: add handling in add / remove watchlist item ?
        contentIdsIncludedToWatchlist: populateContentIdsSet(
          content,
          state.contentIdsIncludedToWatchlist,
        ),
        watchlistIsStale: false,
        mappingWasInitialized: true,
      };
    }
    case 'REQUEST_WATCHLIST_ERROR': {
      const { list } = state;
      return {
        ...state,
        list: {
          /* Keep contents of watchlist even if new request for watchlist items fails */
          ...list,
          fetching: false,
          error: true,
        },
      };
    }
    case 'REQUEST_ADD_TO_WATCH_LIST': {
      const { contentId } = action;
      return {
        ...state,
        contentIdsIncludedToWatchlist: {
          ...state.contentIdsIncludedToWatchlist,
          /* Mark contentId as added to the watchlist immediately */
          [contentId]: true,
        },
      };
    }
    case 'RECEIVE_ADD_TO_WATCH_LIST': {
      return { ...state, watchlistIsStale: true };
    }
    case 'REQUEST_ADD_TO_WATCH_LIST_ERROR': {
      const { contentId } = action;
      return {
        ...state,
        contentIdsIncludedToWatchlist: {
          ...state.contentIdsIncludedToWatchlist,
          /* Mark contentId as deleted from the watchlist since request failed */
          [contentId]: false,
        },
      };
    }
    case 'REQUEST_REMOVE_ITEM_FROM_WATCHLIST': {
      const { contentId } = action;
      return {
        ...state,
        list: {
          ...state.list,
          content: filterOutRemovedWatchlistItem(state.list.content, contentId),
        },
        contentIdsIncludedToWatchlist: {
          ...state.contentIdsIncludedToWatchlist,
          [contentId]: false,
        },
      };
    }
    case 'RECEIVE_REMOVE_ITEM_FROM_WATCHLIST': {
      return { ...state };
    }
    case 'REQUEST_REMOVE_ITEM_FROM_WATCHLIST_ERROR': {
      const { contentId } = action;
      return {
        ...state,
        contentIdsIncludedToWatchlist: {
          ...state.contentIdsIncludedToWatchlist,
          /* Mark contentId as added into watchlist, since request failed */
          [contentId]: true,
        },
      };
    }
    case 'REQUEST_REMOVE_ALL_FROM_WATCHLIST': {
      return {
        ...state,
        list: {
          fetching: false,
          error: false,
          content: [],
        },
      };
    }
    case 'RECEIVE_REMOVE_ALL_FROM_WATCHLIST': {
      return { ...state, contentIdsIncludedToWatchlist: {} };
    }
    case 'REQUEST_REMOVE_ALL_FROM_WATCHLIST_ERROR': {
      return { ...state, watchlistIsStale: true };
    }
    case 'WATCHLIST_SORT': {
      const { payload: requestedSort } = action;
      const { list } = state;

      /* Return new copy of state if requested sorting match current sorting */

      switch (requestedSort) {
        case 'name': {
          return {
            ...state,
            sort: 'name',
            list: { ...list, content: sortWatchlistByName(list.content) },
          };
        }
        case 'date_added': {
          return {
            ...state,
            sort: 'date_added',
            list: {
              ...list,
              content: sortWatchListByDateAdded(list.content),
            },
          };
        }
        default: {
          (requestedSort: empty); // eslint-disable-line
          return state;
        }
      }
    }
    // $FlowExpectedError
    case 'RECEIVE_SET_CURRENT_PROFILE_ID': {
      return { ...state, watchlistIsStale: true, contentIdsIncludedToWatchlist: {} };
    }
    // $FlowExpectedError
    case 'SIGN_OUT': {
      return initialState;
    }
    case 'WATCHLIST_FORCE_LOAD': {
      return { ...state, watchlistIsStale: true };
    }
    default:
      /* Following line assures switch exhaustiveness */
      (action.type: empty); /* eslint-disable-line */
      return state;
  }
};

export default reducer;
